/**
 * The microphone publish path: raw capture → RNNoise worklet → published track.
 *
 *   getUserMedia ─▶ MediaStreamAudioSourceNode ─▶ RnnoiseWorkletNode ─▶ dest
 *                                                                        │
 *                                            the track LiveKit publishes ◀┘
 *
 * The pipeline is OPTIONAL by design: `create` returns null whenever RNNoise is
 * not wanted or not available, and the caller publishes the raw track, which
 * already carries the native `noiseSuppression` constraint. There is no state
 * in which the user loses their microphone because a denoiser misbehaved.
 */
import type { DenoiseRuntime, AudioProcessingPreferences } from '../settings/preferences';
import { applyNativeProcessing } from './audioConstraints';
import { RNNOISE_SAMPLE_RATE, createRnnoiseNode } from './rnnoise';

export interface AudioPipeline {
  /** The processed track to publish. */
  readonly track: MediaStreamTrack;
  readonly runtime: DenoiseRuntime;
  stop(): void;
}

/** Autoplay policy can leave a fresh context suspended; resume on a gesture. */
function resumeOnGesture(context: AudioContext): void {
  const events: readonly string[] = ['pointerdown', 'keydown'];
  const resume = (): void => {
    for (const event of events) document.removeEventListener(event, resume);
    void context.resume().catch(() => undefined);
  };
  for (const event of events) document.addEventListener(event, resume, { once: true });
}

/**
 * Wraps a captured microphone stream in the RNNoise worklet.
 * Returns null when the caller should just publish the raw track.
 */
export async function createAudioPipeline(
  source: MediaStream,
  preferences: AudioProcessingPreferences,
): Promise<AudioPipeline | null> {
  const raw = source.getAudioTracks()[0];
  if (!raw || preferences.noiseSuppression !== 'rnnoise') return null;

  let context: AudioContext;
  try {
    context = new AudioContext({ sampleRate: RNNOISE_SAMPLE_RATE, latencyHint: 'interactive' });
  } catch {
    // Some devices refuse a forced 48 kHz context — native suppression it is.
    return null;
  }

  const node = await createRnnoiseNode(context);
  const destination = node ? context.createMediaStreamDestination() : null;
  const processed = destination?.stream.getAudioTracks()[0] ?? null;

  if (!node || !destination || !processed) {
    node?.destroy();
    void context.close().catch(() => undefined);
    return null;
  }

  const input = context.createMediaStreamSource(source);
  input.connect(node);
  node.connect(destination);

  // RNNoise now owns denoising — stop the browser doing it a second time.
  await applyNativeProcessing(raw, preferences, false);

  if (context.state === 'suspended') {
    await context.resume().catch(() => undefined);
    if (context.state === 'suspended') resumeOnGesture(context);
  }

  return {
    track: processed,
    runtime: 'rnnoise',
    stop() {
      input.disconnect();
      node.disconnect();
      node.destroy();
      processed.stop();
      void context.close().catch(() => undefined);
    },
  };
}

/** What the UI should report when no worklet is running. */
export function fallbackRuntime(preferences: AudioProcessingPreferences): DenoiseRuntime {
  if (preferences.noiseSuppression === 'off') return 'off';
  return preferences.noiseSuppression === 'rnnoise' ? 'unavailable' : 'native';
}
